import React, {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {
    GT06_STALE_THRESHOLD_MS,
    ONLINE_THRESHOLD_MS,
    isFt920,
    isGt06Stale,
    showAlertBell,
} from "../../utils/deviceStatus";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAnglesLeft,
    faAnglesRight,
    faBan,
    faBatteryHalf,
    faBolt,
    faChevronLeft,
    faChevronRight,
    faCircleQuestion,
    faClock,
    faLayerGroup,
    faList,
    faLock,
    faLockOpen,
    faMapLocationDot,
    faRotate,
    faRoute,
    faSearch,
    faSort,
    faSortDown,
    faSortUp,
    faTimes,
    faTruckFast,
    faUnlock,
    faWifi,
} from "@fortawesome/free-solid-svg-icons";
import {
    API_AUTH_V1,
    API_V1,
    BASE_URL,
    BEARER,
    DEVICE_ONLINE,
    LOCKED,
    NO_INFO,
    OPEN,
} from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, getGeolocationFromLatLng} from "../../utils/utility";
import {hasPermission} from "../../utils/rolePermissions";
import {logoutFun} from "../HomeSideNavBar";
import {convertUtcToLocal} from "../../utils/dateUtils";
import {subscribeMobiTrack, isMobiTrackLive, seedMobiTrack} from "../../utils/mobiTrackRealtime";
import {
    DEFAULT_DEVICE_TAB_ORDER,
    loadTabOrder,
    moveTab,
    resetTabOrder,
    saveTabOrder,
} from "../../utils/dashboardTabOrder";
import {AutoSizer, List as VirtualList} from "react-virtualized";
import "react-virtualized/styles.css";


const DEVICE_TABS = {
    GT06_TRACKER: {key: "GT06_TRACKER", label: "GPS Tracker", icon: faTruckFast},
    GPS_LOCK: {key: "GPS_LOCK", label: "GPS Lock", icon: faMapLocationDot},
    BT_LOCK: {key: "BT_LOCK", label: "BT Lock", icon: faLock},
    MOBI_TRACK: {key: "MOBI_TRACK", label: "Track Point", icon: faRoute},
};

// Device online/staleness/protocol rules live in src/utils/deviceStatus.js so the
// list, the details panel and the dashboard insights can't drift apart.

const QUICK_FILTERS_BY_TAB = {
    GPS_LOCK: [
        {key: "DEFAULT", label: "All", icon: faLayerGroup, countKey: "DEFAULT", tooltip: "Show all GPS Lock devices"},
        {key: "ONLINE", label: "Online", icon: faWifi, countKey: "ONLINE", tooltip: "Devices actively connected"},
        {key: "OFFLINE", label: "Offline", icon: faBan, countKey: "OFFLINE", tooltip: "Devices not connected"},
        {key: "MOTION", label: "Motion", icon: faRoute, countKey: "MOTION", tooltip: "Devices currently moving"},
        {key: "IDLE", label: "Idle", icon: faClock, countKey: "IDLE", tooltip: "Stationary devices"},
        {key: "LOCKED", label: "Locked", icon: faLock, countKey: "LOCKED", tooltip: "Devices with lock engaged"},
        {key: "UNLOCKED", label: "Unlocked", icon: faLockOpen, countKey: "UNLOCKED", tooltip: "Devices with lock disengaged"},
    ],
    BT_LOCK: [
        {key: "DEFAULT", label: "All", icon: faLayerGroup, countKey: "DEFAULT", tooltip: "Show all BT Lock devices"},
        {key: "LOCKED", label: "Locked", icon: faLock, countKey: "LOCKED", tooltip: "Devices with lock engaged"},
        {key: "UNLOCKED", label: "Unlocked", icon: faLockOpen, countKey: "UNLOCKED", tooltip: "Devices with lock disengaged"},
    ],
    MOBI_TRACK: [
        {key: "DEFAULT", label: "All", icon: faLayerGroup, countKey: "DEFAULT", tooltip: "Show all Track Point devices"},
        {key: "ONLINE", label: "Online", icon: faWifi, countKey: "ONLINE", tooltip: "Devices that posted recently"},
        {key: "OFFLINE", label: "Offline", icon: faBan, countKey: "OFFLINE", tooltip: "Devices inactive for 3+ minutes"},
    ],
    GT06_TRACKER: [
        {key: "DEFAULT", label: "All", icon: faLayerGroup, countKey: "DEFAULT", tooltip: "Show all GPS Tracker devices"},
        {key: "ONLINE", label: "Online", icon: faWifi, countKey: "ONLINE", tooltip: "Devices connected"},
        {key: "OFFLINE", label: "Offline", icon: faBan, countKey: "OFFLINE", tooltip: "Devices not connected"},
        {key: "ENGINE_ON", label: "Ignition On", icon: faBolt, countKey: "ENGINE_ON", tooltip: "Vehicles with ignition on"},
        {key: "ENGINE_OFF", label: "Ignition Off", icon: faBan, countKey: "ENGINE_OFF", tooltip: "Vehicles with ignition off"},
    ],
};

const GT06_SMS_COMMANDS = [
    {cmd: "APN,000000,apn,user,pass#", desc: "Set mobile network APN", example: "APN,000000,internet,,#", response: "APN OK"},
    {cmd: "SERVER,1,IP,PORT,0#", desc: "Set tracking server address", example: "SERVER,1,track.example.com,6000,0#", response: "SEVER OK"},
    {cmd: "GPRS#", desc: "Connect to GPRS / reconnect to server", example: "GPRS#", response: "GPRS OK"},
    {cmd: "TIMER,000000,seconds#", desc: "Set GPS report interval (10–18000 s)", example: "TIMER,000000,30#", response: "TIMER OK"},
    {cmd: "SLEEP,000000,mode#", desc: "Sleep mode: 0=off, 1=vibration, 2=time", example: "SLEEP,000000,0#", response: "SLEEP OK"},
    {cmd: "SOS,A,num1,num2,num3#", desc: "Set SOS alert phone numbers (A=add)", example: "SOS,A,+1234567890,,#", response: "SOS OK"},
    {cmd: "MONITOR,A,number#", desc: "Add silent monitor phone number", example: "MONITOR,A,+1234567890#", response: "MONITOR OK"},
    {cmd: "SPEED,000000,kmh,0#", desc: "Overspeed alert threshold (0 = disable)", example: "SPEED,000000,120,0#", response: "SPEED OK"},
    {cmd: "DEFENSE,1#", desc: "Arm vibration alarm", example: "DEFENSE,1#", response: "DEFENSE ON"},
    {cmd: "DEFENSE,0#", desc: "Disarm vibration alarm", example: "DEFENSE,0#", response: "DEFENSE OFF"},
    {cmd: "DYD,000000#", desc: "Cut engine relay (blocked if speed > 20 km/h)", example: "DYD,000000#", response: "DYD OK"},
    {cmd: "HFYD,000000#", desc: "Restore engine relay", example: "HFYD,000000#", response: "HFYD OK"},
    {cmd: "STATUS#", desc: "Query device status (battery, signal, GPS)", example: "STATUS#", response: "GSM:4 GPS:A Bat:4.1V..."},
    {cmd: "PARAM#", desc: "Query all current settings", example: "PARAM#", response: "APN:... SERVER:..."},
    {cmd: "IMEI#", desc: "Query device IMEI number", example: "IMEI#", response: "IMEI:123456789012345"},
    {cmd: "TIMEZONE,000000,offset#", desc: "Set timezone offset in hours (e.g. 8 = UTC+8)", example: "TIMEZONE,000000,0#", response: "TIMEZONE OK"},
    {cmd: "PASSWORD,old,new#", desc: "Change SMS password (default: 000000)", example: "PASSWORD,000000,123456#", response: "PASSWORD OK"},
    {cmd: "FACTORY,000000#", desc: "Factory reset all settings", example: "FACTORY,000000#", response: "FACTORY OK"},
];

const GT06HelpModal = ({onClose}) => {
    const [activeSection, setActiveSection] = useState("setup");

    const sections = [
        {key: "setup", label: "Quick Setup"},
        {key: "commands", label: "SMS Commands"},
        {key: "relay", label: "Relay Control"},
        {key: "troubleshoot", label: "Troubleshooting"},
    ];

    return (
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 9999,
                background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "16px",
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div style={{
                background: "#fff", borderRadius: "12px", width: "100%", maxWidth: "820px",
                maxHeight: "88vh", display: "flex", flexDirection: "column",
                boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}>
                {/* Modal header */}
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "16px 20px", borderBottom: "1px solid #e5e7eb",
                    background: "linear-gradient(135deg, #363166 0%, #4a4490 100%)",
                    borderRadius: "12px 12px 0 0",
                }}>
                    <div style={{display: "flex", alignItems: "center", gap: "10px"}}>
                        <FontAwesomeIcon icon={faTruckFast} style={{color: "#FF683A", fontSize: "1.2rem"}}/>
                        <div>
                            <div style={{color: "#fff", fontWeight: 700, fontSize: "1rem"}}>GPS Tracker Setup Guide</div>
                            <div style={{color: "rgba(255,255,255,0.65)", fontSize: "0.75rem"}}>GT06 / Concox — SMS Configuration</div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: "rgba(255,255,255,0.15)", border: "none", borderRadius: "6px",
                            color: "#fff", cursor: "pointer", padding: "6px 10px", fontSize: "1rem",
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes}/>
                    </button>
                </div>

                {/* Section tabs */}
                <div style={{display: "flex", gap: "6px", padding: "12px 20px 0", borderBottom: "1px solid #e5e7eb"}}>
                    {sections.map((s) => (
                        <button
                            key={s.key}
                            onClick={() => setActiveSection(s.key)}
                            style={{
                                padding: "7px 14px", border: "1.5px solid",
                                borderColor: activeSection === s.key ? "#FF683A" : "rgba(54,49,102,0.25)",
                                borderBottom: activeSection === s.key ? "1.5px solid #fff" : "1.5px solid rgba(54,49,102,0.25)",
                                marginBottom: activeSection === s.key ? "-1px" : "0",
                                borderRadius: "6px 6px 0 0",
                                background: activeSection === s.key ? "#fff" : "rgba(54,49,102,0.04)",
                                color: activeSection === s.key ? "#FF683A" : "#363166",
                                fontWeight: activeSection === s.key ? 600 : 400,
                                fontSize: "0.82rem", cursor: "pointer",
                            }}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>

                {/* Modal body */}
                <div style={{overflowY: "auto", padding: "20px", flex: 1}}>

                    {/* ── Quick Setup ── */}
                    {activeSection === "setup" && (
                        <div>
                            <p style={{margin: "0 0 14px", color: "#374151", fontSize: "0.88rem"}}>
                                SMS command format: <code style={codeStyle}>COMMAND,password,params#</code>
                                &nbsp;— default password is <code style={codeStyle}>000000</code>. Send from any authorised number.
                            </p>
                            <table style={tableStyle}>
                                <thead>
                                    <tr style={{background: "#363166"}}>
                                        <th style={{...thStyle, width: "28px"}}>#</th>
                                        <th style={thStyle}>Step</th>
                                        <th style={thStyle}>SMS to send</th>
                                        <th style={thStyle}>Expected reply</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        ["1", "Factory reset (start clean)", "FACTORY,000000#", "FACTORY OK"],
                                        ["2", "Set APN for SIM carrier", "APN,000000,internet,,#", "APN OK"],
                                        ["3", "Point to tracking server", "SERVER,1,your.server.com,6000,0#", "SEVER OK"],
                                        ["4", "Connect to GPRS", "GPRS#", "GPRS OK"],
                                        ["5", "Set report interval (30 s)", "TIMER,000000,30#", "TIMER OK"],
                                        ["6", "Disable sleep mode", "SLEEP,000000,0#", "SLEEP OK"],
                                        ["7", "Set SOS number", "SOS,A,+1234567890,,#", "SOS OK"],
                                        ["8", "Check device status", "STATUS#", "GSM/GPS/battery info"],
                                        ["9", "Query all settings", "PARAM#", "Full config dump"],
                                    ].map(([n, step, sms, reply]) => (
                                        <tr key={n} style={{borderBottom: "1px solid #f0f0f0"}}>
                                            <td style={{...tdStyle, color: "#FF683A", fontWeight: 700, textAlign: "center"}}>{n}</td>
                                            <td style={{...tdStyle, color: "#374151"}}>{step}</td>
                                            <td style={tdStyle}><code style={codeStyle}>{sms}</code></td>
                                            <td style={{...tdStyle, color: "#6b7280", fontSize: "0.8rem"}}>{reply}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div style={infoBoxStyle}>
                                <strong>Tip:</strong> Replace <code style={codeStyle}>internet</code> with your SIM card&apos;s actual APN (e.g. <code style={codeStyle}>data.carrier.com</code>). Leave user/pass blank for most carriers.
                            </div>
                        </div>
                    )}

                    {/* ── SMS Commands ── */}
                    {activeSection === "commands" && (
                        <div>
                            <p style={{margin: "0 0 14px", color: "#374151", fontSize: "0.88rem"}}>
                                Complete command reference. All commands use the format <code style={codeStyle}>COMMAND,password,params#</code>.
                            </p>
                            <table style={tableStyle}>
                                <thead>
                                    <tr style={{background: "#363166"}}>
                                        <th style={thStyle}>Command</th>
                                        <th style={thStyle}>Description</th>
                                        <th style={thStyle}>Example</th>
                                        <th style={thStyle}>Reply</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {GT06_SMS_COMMANDS.map((row, i) => (
                                        <tr key={i} style={{borderBottom: "1px solid #f0f0f0", background: i % 2 === 0 ? "#fff" : "#fafafa"}}>
                                            <td style={tdStyle}><code style={{...codeStyle, whiteSpace: "nowrap"}}>{row.cmd}</code></td>
                                            <td style={{...tdStyle, color: "#374151", fontSize: "0.82rem"}}>{row.desc}</td>
                                            <td style={tdStyle}><code style={{...codeStyle, fontSize: "0.75rem"}}>{row.example}</code></td>
                                            <td style={{...tdStyle, color: "#6b7280", fontSize: "0.78rem", whiteSpace: "nowrap"}}>{row.response}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* ── Relay Control ── */}
                    {activeSection === "relay" && (
                        <div style={{display: "flex", flexDirection: "column", gap: "14px"}}>
                            <div style={infoBoxStyle}>
                                <strong>Safety lock:</strong> The relay cut command is blocked if the vehicle speed is above 20 km/h or if there is no valid GPS fix. Always confirm <code style={codeStyle}>STATUS#</code> before cutting.
                            </div>
                            {[
                                {
                                    title: "Cut Engine Relay",
                                    cmd: "DYD,000000#",
                                    reply: "DYD OK",
                                    color: "#dc2626",
                                    bg: "#fef2f2",
                                    border: "#fecaca",
                                    steps: [
                                        "Send STATUS# — confirm GPS fix and speed < 20 km/h",
                                        "Send DYD,000000# to cut fuel/ignition relay",
                                        "Device replies DYD OK when relay is open",
                                        "Vehicle engine will stop at next ignition cycle",
                                    ],
                                },
                                {
                                    title: "Restore Engine Relay",
                                    cmd: "HFYD,000000#",
                                    reply: "HFYD OK",
                                    color: "#16a34a",
                                    bg: "#f0fdf4",
                                    border: "#bbf7d0",
                                    steps: [
                                        "Send HFYD,000000# to close the relay",
                                        "Device replies HFYD OK when relay is restored",
                                        "Vehicle can now start normally",
                                    ],
                                },
                            ].map((item) => (
                                <div key={item.title} style={{border: `1px solid ${item.border}`, borderRadius: "8px", background: item.bg, padding: "14px 16px"}}>
                                    <div style={{display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px"}}>
                                        <span style={{fontWeight: 700, color: item.color, fontSize: "0.95rem"}}>{item.title}</span>
                                        <code style={{...codeStyle, background: item.color, color: "#fff", border: "none"}}>{item.cmd}</code>
                                        <span style={{fontSize: "0.78rem", color: item.color}}>→ {item.reply}</span>
                                    </div>
                                    <ol style={{margin: 0, paddingLeft: "18px", color: "#374151", fontSize: "0.85rem", lineHeight: "1.7"}}>
                                        {item.steps.map((s, i) => <li key={i}>{s}</li>)}
                                    </ol>
                                </div>
                            ))}
                            <div style={{...infoBoxStyle, background: "#fffbeb", borderColor: "#fde68a"}}>
                                <strong>Platform commands (TCP):</strong> The server can also send relay commands directly over the TCP connection without SMS — use <code style={codeStyle}>DYD#</code> / <code style={codeStyle}>HFYD#</code> from the Command Center.
                            </div>
                        </div>
                    )}

                    {/* ── Troubleshooting ── */}
                    {activeSection === "troubleshoot" && (
                        <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
                            {[
                                {
                                    problem: "Device not appearing online",
                                    checks: [
                                        {q: "SIM has data?", fix: "Insert SIM with active data plan; check APN: send APN,000000,apn,,#"},
                                        {q: "Server address correct?", fix: "Send PARAM# and verify SERVER line matches your platform IP/port"},
                                        {q: "GPRS connected?", fix: "Send GPRS# to force reconnect"},
                                        {q: "Power OK?", fix: "Send STATUS# — check battery voltage (should be > 3.6 V) and GSM signal (should be > 1)"},
                                    ],
                                },
                                {
                                    problem: "No location / GPS not fixing",
                                    checks: [
                                        {q: "Device outdoors?", fix: "GT06 requires open sky — first fix can take 2–5 min outdoors"},
                                        {q: "GPS signal in STATUS#?", fix: "STATUS# shows GPS:A (active) or GPS:V (void). V means no fix yet"},
                                        {q: "Firmware issue?", fix: "Try FACTORY,000000# to reset, then reconfigure"},
                                    ],
                                },
                                {
                                    problem: "Relay cut not working",
                                    checks: [
                                        {q: "Speed > 20 km/h?", fix: "Cut is blocked above 20 km/h — wait until vehicle is stationary"},
                                        {q: "No GPS fix?", fix: "Device requires GPS:A in STATUS# before allowing relay cut"},
                                        {q: "Relay wired correctly?", fix: "Verify relay wiring: power through relay normally-closed contact"},
                                    ],
                                },
                                {
                                    problem: "SMS commands not responding",
                                    checks: [
                                        {q: "Sending from authorised number?", fix: "SOS numbers can send commands; if set, random numbers are ignored"},
                                        {q: "Correct password?", fix: "Default is 000000 — if changed, use new password or factory reset"},
                                        {q: "Correct format?", fix: "Every command must end with # — e.g. STATUS# not STATUS"},
                                    ],
                                },
                            ].map((item) => (
                                <div key={item.problem} style={{border: "1px solid rgba(54,49,102,0.2)", borderRadius: "8px", overflow: "hidden"}}>
                                    <div style={{background: "#363166", color: "#fff", padding: "8px 14px", fontWeight: 600, fontSize: "0.88rem"}}>
                                        {item.problem}
                                    </div>
                                    <table style={{...tableStyle, margin: 0, borderRadius: 0}}>
                                        <thead>
                                            <tr style={{background: "rgba(54,49,102,0.06)"}}>
                                                <th style={{...thStyle, background: "transparent", color: "#363166", width: "35%"}}>Check</th>
                                                <th style={{...thStyle, background: "transparent", color: "#363166"}}>Fix</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {item.checks.map((c, i) => (
                                                <tr key={i} style={{borderBottom: "1px solid #f0f0f0"}}>
                                                    <td style={{...tdStyle, color: "#6b7280", fontSize: "0.82rem"}}>{c.q}</td>
                                                    <td style={{...tdStyle, color: "#374151", fontSize: "0.82rem"}}>{c.fix}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const codeStyle = {
    background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: "4px",
    padding: "1px 6px", fontFamily: "monospace", fontSize: "0.82rem", color: "#363166",
};
const tableStyle = {
    width: "100%", borderCollapse: "collapse", fontSize: "0.83rem",
    border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden",
};
const thStyle = {
    padding: "8px 12px", textAlign: "left", fontWeight: 600,
    color: "#fff", background: "#363166", fontSize: "0.8rem",
};
const tdStyle = {padding: "7px 12px", verticalAlign: "top"};
const infoBoxStyle = {
    background: "#f0f4ff", border: "1px solid rgba(54,49,102,0.25)",
    borderRadius: "8px", padding: "10px 14px", fontSize: "0.84rem", color: "#374151",
};

const DeviceList = ({
    devices,
    selectedDeviceId,
    locationMap,
    isRefreshingLocation,
    onRefreshLocation,
    onRowClick,
    searchQuery,
    setSearchQuery,
    activeDeviceTab,
    setActiveDeviceTab,
    deviceTabCounts,
    onResetFilters,
    viewMode,
    setViewMode,
    quickFilter,
    setQuickFilter,
    deviceTypeActions,
    labelFilter,
    passcodeFeature,
    onInstantUnlock,
}) => {

    const navigate = useNavigate();
    const isFilterActive = searchQuery !== "" || quickFilter !== "DEFAULT";
    const [sortConfig, setSortConfig] = useState({key: null, direction: null});
    const [currentPage, setCurrentPage] = useState(1);

    // ── Device-type tab order: user-arrangeable via drag & drop, saved per user ──
    const [tabOrder, setTabOrder] = useState(loadTabOrder);
    const [dragKey, setDragKey] = useState(null);
    const [dragOverKey, setDragOverKey] = useState(null);
    const isDefaultTabOrder = tabOrder.every((k, i) => k === DEFAULT_DEVICE_TAB_ORDER[i]);
    const applyTabOrder = (next, persist = true) => {
        if (next === tabOrder) return;
        console.info("[Dashboard] Device type tab order →", next.join(", "));
        setTabOrder(next);
        if (persist) saveTabOrder(next);
    };

    // ── Bulk selection for group operations (GPS Lock tab only) ─────────────
    // Both Schedule Unlock and Instant Unlock act on a group of GPS locks. Show
    // the selection UI when the user can do at least one of them; each button is
    // then gated by its own permission (both are manager-and-above).
    const canScheduleUnlock = hasPermission("sidebar_scheduledUnlocks");
    const canInstantUnlock = hasPermission("commandCenter_send");
    const bulkSelectionEnabled =
        activeDeviceTab === "GPS_LOCK" && (canScheduleUnlock || canInstantUnlock);
    const [bulkSelectedIds, setBulkSelectedIds] = useState(() => new Set());

    // Clear the selection whenever the device-type tab changes.
    React.useEffect(() => {
        setBulkSelectedIds(new Set());
    }, [activeDeviceTab]);

    const toggleBulkSelect = (unitId, e) => {
        if (e) e.stopPropagation();
        setBulkSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(unitId)) next.delete(unitId); else next.add(unitId);
            return next;
        });
    };

    const scheduleSelectedLocks = () => {
        if (bulkSelectedIds.size === 0) return;
        navigate("/scheduled-unlocks", {state: {presetAssetUnitIds: Array.from(bulkSelectedIds)}});
    };

    // Select / clear all currently-filtered locks (respects the quick filter,
    // search and label filter — i.e. exactly what the user is looking at).
    const toggleSelectAllFiltered = () => {
        const filteredIds = sortedDevices.map((d) => d.assetUnitId);
        setBulkSelectedIds((prev) => {
            const everySelected = filteredIds.length > 0 && filteredIds.every((id) => prev.has(id));
            const next = new Set(prev);
            if (everySelected) filteredIds.forEach((id) => next.delete(id));
            else filteredIds.forEach((id) => next.add(id));
            return next;
        });
    };

    // Instant unlock the selected locks now. Only online locks can be unlocked;
    // offline ones are reported and skipped. When the client requires passcode
    // verification, bulk instant unlock is disabled (verification is per-lock).
    const instantUnlockSelected = () => {
        const selected = sortedDevices.filter((d) => bulkSelectedIds.has(d.assetUnitId));
        const online = selected.filter((d) => getDeviceStatus(d) === DEVICE_ONLINE);
        const offlineCount = selected.length - online.length;
        if (online.length === 0) {
            window.alert("None of the selected locks are online. Instant unlock needs the lock connected.");
            return;
        }
        const msg =
            `Unlock ${online.length} lock${online.length > 1 ? "s" : ""} now?` +
            (offlineCount > 0 ? `\n\n${offlineCount} offline lock(s) will be skipped.` : "");
        if (!window.confirm(msg)) return;
        onInstantUnlock && onInstantUnlock(online.map((d) => d.assetUnitId));
        setBulkSelectedIds(new Set());
    };
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [mobiTrackLocations, setMobiTrackLocations] = useState({});
    const [isFetchingMobiTrack, setIsFetchingMobiTrack] = useState(false);
    const [addressCache, setAddressCache] = useState({});
    const [tripMap, setTripMap] = useState({});
    const [showGt06Help, setShowGt06Help] = useState(false);

    React.useEffect(() => {
        const clientId = getClientIdOfUser();
        if (!clientId) return;
        fetch(`${BASE_URL}${API_V1}/${clientId}/${UrlConstants.TRIPS}?page=0&size=200`, {
            headers: { Authorization: `${BEARER} ${getAuthToken()}` },
        })
            .then((r) => r.json())
            .then((d) => {
                const trips = d.responseData?.content || d.responseData || [];
                const map = {};
                trips
                    .filter((t) => t.status === "ACTIVE")
                    .forEach((t) => { (t.deviceIds || []).forEach((id) => { map[id] = t.name; }); });
                setTripMap(map);
            })
            .catch(() => {});
    }, []);

    // Stable key of the MOBI_TRACK unit ids in this list. The dashboard rebuilds the
    // `devices` array reference on every socket frame, so keying the effects below on
    // `devices` directly would re-seed (REST) and re-subscribe on every frame — a
    // hidden poll + subscription churn. Depending on this key instead means they run
    // only when the *set* of Track Point devices actually changes.
    const mobiUnitsKey = React.useMemo(
        () => devices.filter(d => d.assetType === "MOBI_TRACK").map(d => d.assetUnitId).sort().join(","),
        [devices]
    );
    const mobiUnitIds = React.useMemo(
        () => (mobiUnitsKey ? mobiUnitsKey.split(",") : []),
        [mobiUnitsKey]
    );

    // One-time REST seed of Track Point presence (initial paint only). Merges with
    // prev so a slow seed can't clobber fresher socket updates that already landed.
    React.useEffect(() => {
        if (mobiUnitIds.length === 0) return;
        let cancelled = false;
        const seed = async () => {
            setIsFetchingMobiTrack(true);
            const seeded = {};
            for (const unit of mobiUnitIds) {
                try {
                    const endPoint = `${API_AUTH_V1}/mobi-track/latest/${unit}`;
                    axios.defaults.headers.common["Authorization"] = `${BEARER} ${getAuthToken()}`;
                    const response = await axios.get(`${BASE_URL}${endPoint}`);
                    if (response.status === 200) {
                        const locationData = response.data;
                        // Feed the shared store so presence has one authority (list + detail agree).
                        seedMobiTrack(unit, locationData);
                        seeded[unit] = {
                            latitude: locationData.latitude,
                            longitude: locationData.longitude,
                            timestamp: locationData.timestamp,
                            formattedTime: convertUtcToLocal(locationData.timestamp),
                            status: isMobiTrackLive(unit) ? "ONLINE" : "OFFLINE",
                            sessionId: locationData.sessionId,
                        };
                    } else if (response.status === 403) {
                        logoutFun();
                    }
                } catch (error) {
                    if (error.response && error.response.status === 403) logoutFun();
                }
            }
            if (!cancelled) {
                // prev (live socket data) wins over the seed for any overlapping unit.
                setMobiTrackLocations(prev => ({ ...seeded, ...prev }));
                setIsFetchingMobiTrack(false);
            }
        };
        seed();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobiUnitsKey]);

    // Live Track Point presence over WebSocket. From here on, each MOBI_TRACK frame
    // flips a row ONLINE and updates its position, and a local (no-network) sweep
    // flips a row OFFLINE once the socket has gone silent for a device we'd previously
    // seen live. Only MOBI_TRACK is touched — GT06/lock rows are unaffected.
    React.useEffect(() => {
        if (mobiUnitIds.length === 0) return;

        const unsubs = mobiUnitIds.map(unit => {
            return subscribeMobiTrack(unit, (frame) => {
                setMobiTrackLocations(prev => ({
                    ...prev,
                    [unit]: {
                        // On a duty-off frame keep the last coordinates but flip the row
                        // OFFLINE immediately — mirrors how it flips ONLINE on a live frame.
                        latitude: frame.latitude != null ? frame.latitude : prev[unit]?.latitude,
                        longitude: frame.longitude != null ? frame.longitude : prev[unit]?.longitude,
                        timestamp: frame.timestamp,
                        formattedTime: frame.timestamp
                            ? convertUtcToLocal(frame.timestamp)
                            : prev[unit]?.formattedTime,
                        status: frame.offline ? "OFFLINE" : "ONLINE",
                        sessionId: prev[unit]?.sessionId,
                    },
                }));
            });
        });

        // Reconcile every row against the single presence authority (isMobiTrackLive) on a tick.
        // This is what makes list and detail agree and fixes "stuck ONLINE": it re-derives status
        // for EVERY unit — including REST-seeded rows never seen on the socket — so a driver whose
        // window has lapsed flips OFFLINE, and one who is live flips ONLINE, without special-casing.
        const sweep = setInterval(() => {
            setMobiTrackLocations(prev => {
                let changed = false;
                const next = {...prev};
                for (const unit of mobiUnitIds) {
                    const loc = next[unit];
                    if (!loc) continue;
                    const status = isMobiTrackLive(unit) ? "ONLINE" : "OFFLINE";
                    if (loc.status !== status) {
                        next[unit] = {...loc, status};
                        changed = true;
                    }
                }
                return changed ? next : prev;
            });
        }, 15000);

        return () => {
            unsubs.forEach(u => u && u());
            clearInterval(sweep);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobiUnitsKey]);

    // On socket reconnect the store re-requests current presence from the server over the socket
    // (see mobiTrackRealtime.requestMobiTrackStatus) — those re-emitted frames flow through the
    // subscription above and update the rows, so no REST re-seed is needed here.

    React.useEffect(() => {
        const resolveAddresses = async () => {
            for (const [, loc] of Object.entries(mobiTrackLocations)) {
                if (!loc?.latitude || !loc?.longitude) continue;
                const cacheKey = `${loc.latitude},${loc.longitude}`;
                if (addressCache[cacheKey]) continue;
                setAddressCache(prev => ({...prev, [cacheKey]: "Resolving address..."}));
                const address = await getGeolocationFromLatLng(loc.latitude, loc.longitude);
                setAddressCache(prev => ({...prev, [cacheKey]: address || "Address not available"}));
            }
        };
        if (Object.keys(mobiTrackLocations).length > 0) resolveAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mobiTrackLocations]);

    const getDisplayLocation = React.useCallback((device) => {
        if (device.assetType === "MOBI_TRACK") {
            const loc = mobiTrackLocations[device.assetUnitId];
            if (!loc) return "Not Available";
            const cacheKey = `${loc.latitude},${loc.longitude}`;
            return addressCache[cacheKey] || "Resolving address...";
        }
        return locationMap[device.assetUnitId] || "Pending...";
    }, [mobiTrackLocations, addressCache, locationMap]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({key, direction});
        setCurrentPage(1);
    };

    const getDeviceStatus = (device) => {
        if (device.assetType === "MOBI_TRACK") {
            // Read the single presence authority directly so the badge is always current
            // (the 15s reconcile tick above forces the re-render that re-evaluates this).
            return isMobiTrackLive(device.assetUnitId) ? "ONLINE" : "OFFLINE";
        }
        if (device.assetType === "GT06_TRACKER" && isGt06Stale(device.lastFrameUtc)) {
            return "OFFLINE";
        }
        return device.deviceStatusF2;
    };

    const getDeviceLastUpdate = (device) => {
        if (device.assetType === "MOBI_TRACK") {
            const loc = mobiTrackLocations[device.assetUnitId];
            return loc?.formattedTime || "Not Available";
        }
        const ts = device.dateTimeF1;
        if (!ts || ts.includes("NaN")) return "Not Available";
        return ts;
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) return <FontAwesomeIcon icon={faSort} className="sort-icon inactive"/>;
        return sortConfig.direction === 'asc'
            ? <FontAwesomeIcon icon={faSortUp} className="sort-icon active"/>
            : <FontAwesomeIcon icon={faSortDown} className="sort-icon active"/>;
    };

    const getLockBadge = (lockStatus) => {
        const isLocked = lockStatus === "Locked" || lockStatus === "LOCKED";
        return (
            <span className={`lock-status-badge ${isLocked ? "locked" : "unlocked"}`}>
                <FontAwesomeIcon icon={isLocked ? faLock : faUnlock}/>
                {isLocked ? "Locked" : "Unlocked"}
            </span>
        );
    };

    // isOnline: when false always renders Ignition OFF regardless of stored accOn (stale last-report data)
    const getEngineBadge = (accOn, isOnline) => {
        const on = isOnline && accOn;
        return (
            <span className={`lock-status-badge ${on ? "badge-online" : "badge-offline"}`}>
                <FontAwesomeIcon icon={on ? faBolt : faBan}/>
                {on ? "Ignition ON" : "Ignition OFF"}
            </span>
        );
    };

    const effectiveDevices = useMemo(() => {
        if (activeDeviceTab === "MOBI_TRACK") {
            if (quickFilter === "ONLINE") return devices.filter(d => mobiTrackLocations[d.assetUnitId]?.status === "ONLINE");
            if (quickFilter === "OFFLINE") return devices.filter(d => mobiTrackLocations[d.assetUnitId]?.status !== "ONLINE");
        }
        return devices;
    }, [devices, activeDeviceTab, quickFilter, mobiTrackLocations]);

    const tabQuickFilterCounts = useMemo(() => {
        if (activeDeviceTab === "GPS_LOCK") {
            const onlineCount = devices.filter(d => d.deviceStatusF2 === DEVICE_ONLINE).length;
            const isMoving = (d) => typeof d.assetMovementStatusF5 === "string" && d.assetMovementStatusF5.toLowerCase().includes("moving");
            const isIdle = (d) => d.assetMovementStatusF5 === "Idle" || d.assetMovementStatusF5 === NO_INFO || d.assetMovementStatusF5 === "N/A" || d.assetMovementStatusF5 === "NA";
            const isLocked = (d) => d.lockStatusF4 === LOCKED || d.lockStatusF4 === "LOCKED";
            const isUnlocked = (d) => d.lockStatusF4 === OPEN || d.lockStatusF4 === "Open";
            return {
                DEFAULT: devices.length,
                ONLINE: onlineCount,
                OFFLINE: devices.length - onlineCount,
                MOTION: devices.filter(isMoving).length,
                IDLE: devices.filter(isIdle).length,
                LOCKED: devices.filter(isLocked).length,
                UNLOCKED: devices.filter(isUnlocked).length,
            };
        }
        if (activeDeviceTab === "BT_LOCK") {
            const isLocked = (d) => d.lockStatusF4 === LOCKED || d.lockStatusF4 === "LOCKED";
            const isUnlocked = (d) => d.lockStatusF4 === OPEN || d.lockStatusF4 === "Open";
            return {
                DEFAULT: devices.length,
                LOCKED: devices.filter(isLocked).length,
                UNLOCKED: devices.filter(isUnlocked).length,
            };
        }
        if (activeDeviceTab === "MOBI_TRACK") {
            const onlineCount = devices.filter(d => mobiTrackLocations[d.assetUnitId]?.status === "ONLINE").length;
            return {
                DEFAULT: devices.length,
                ONLINE: onlineCount,
                OFFLINE: devices.length - onlineCount,
            };
        }
        if (activeDeviceTab === "GT06_TRACKER") {
            const onlineCount = devices.filter(d => d.deviceStatusF2 === DEVICE_ONLINE).length;
            // Only count online devices with ignition on — offline devices have stale accOn from last report
            const engineOnCount = devices.filter(d => d.accOn === true && d.deviceStatusF2 === DEVICE_ONLINE).length;
            return {
                DEFAULT: devices.length,
                ONLINE: onlineCount,
                OFFLINE: devices.length - onlineCount,
                ENGINE_ON: engineOnCount,
                ENGINE_OFF: devices.length - engineOnCount,
            };
        }
        return {DEFAULT: devices.length};
    }, [devices, activeDeviceTab, mobiTrackLocations]);

    const sortedDevices = useMemo(() => {
        if (!sortConfig.key) return effectiveDevices;
        const sorted = [...effectiveDevices].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            if (sortConfig.key === 'location') {
                aValue = getDisplayLocation(a);
                bValue = getDisplayLocation(b);
            }
            aValue = String(aValue || '').toLowerCase();
            bValue = String(bValue || '').toLowerCase();
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [effectiveDevices, sortConfig, getDisplayLocation]);

    const totalItems = sortedDevices.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedDevices = sortedDevices.slice(startIndex, endIndex);

    React.useEffect(() => {
        setCurrentPage(1);
    }, [quickFilter, searchQuery, activeDeviceTab, devices.length]);

    const goToPage = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    const handleRefresh = () => {
        console.info("[Dashboard] Manual location refresh requested", {tab: activeDeviceTab, devices: devices.length});
        onRefreshLocation();
        const fetchMobiTrackLocations = async () => {
            const mobiTrackDevices = devices.filter(d => d.assetType === "MOBI_TRACK");
            if (mobiTrackDevices.length === 0) return;
            setIsFetchingMobiTrack(true);
            const newLocations = {};
            for (const device of mobiTrackDevices) {
                try {
                    const endPoint = `${API_AUTH_V1}/mobi-track/latest/${device.assetUnitId}`;
                    axios.defaults.headers.common["Authorization"] = `${BEARER} ${getAuthToken()}`;
                    const response = await axios.get(`${BASE_URL}${endPoint}`);
                    if (response.status === 200) {
                        const locationData = response.data;
                        seedMobiTrack(device.assetUnitId, locationData);
                        newLocations[device.assetUnitId] = {
                            latitude: locationData.latitude,
                            longitude: locationData.longitude,
                            timestamp: locationData.timestamp,
                            formattedTime: convertUtcToLocal(locationData.timestamp),
                            status: isMobiTrackLive(device.assetUnitId) ? "ONLINE" : "OFFLINE",
                            sessionId: locationData.sessionId,
                        };
                    } else if (response.status === 403) {
                        logoutFun();
                    }
                } catch (error) {
                    console.error(`Error fetching MOBI_TRACK location for ${device.assetUnitId}:`, error);
                }
            }
            setMobiTrackLocations(newLocations);
            setIsFetchingMobiTrack(false);
        };
        fetchMobiTrackLocations();
    };

    const currentFilters = QUICK_FILTERS_BY_TAB[activeDeviceTab] || QUICK_FILTERS_BY_TAB.GPS_LOCK;

    // Select-all state, computed over the currently-filtered locks.
    const allFilteredSelected =
        bulkSelectionEnabled && sortedDevices.length > 0 &&
        sortedDevices.every((d) => bulkSelectedIds.has(d.assetUnitId));
    const someFilteredSelected =
        bulkSelectionEnabled && sortedDevices.some((d) => bulkSelectedIds.has(d.assetUnitId));

    return (
        <div className="grid-div col-central-content central-section">
            <div className="central-content-area">

                {/* GT06 Help Modal */}
                {showGt06Help && <GT06HelpModal onClose={() => setShowGt06Help(false)}/>}

                {/* DEVICE TYPE TAB BAR — drag (or Alt+←/→) to reorder; order is saved per user */}
                <div className="device-type-tab-bar" role="tablist">
                    {tabOrder.map((key) => DEVICE_TABS[key]).map((tab) => (
                        <button
                            key={tab.key}
                            role="tab"
                            aria-selected={activeDeviceTab === tab.key}
                            draggable
                            title="Drag to reorder (or Alt + ←/→)"
                            className={`device-type-tab ${activeDeviceTab === tab.key ? "active" : ""} ${dragKey === tab.key ? "dragging" : ""} ${dragOverKey === tab.key && dragKey !== tab.key ? "drag-over" : ""}`}
                            onClick={() => {
                                console.info("[Dashboard] Device type tab →", tab.key);
                                setActiveDeviceTab(tab.key);
                            }}
                            onDragStart={(e) => {
                                setDragKey(tab.key);
                                e.dataTransfer.effectAllowed = "move";
                                e.dataTransfer.setData("text/plain", tab.key);
                            }}
                            onDragOver={(e) => {
                                if (!dragKey) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "move";
                                if (dragOverKey !== tab.key) setDragOverKey(tab.key);
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                if (dragKey) applyTabOrder(moveTab(tabOrder, dragKey, tab.key));
                                setDragKey(null);
                                setDragOverKey(null);
                            }}
                            onDragEnd={() => {
                                setDragKey(null);
                                setDragOverKey(null);
                            }}
                            onKeyDown={(e) => {
                                if (!e.altKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
                                e.preventDefault();
                                const i = tabOrder.indexOf(tab.key);
                                const j = e.key === "ArrowLeft" ? i - 1 : i + 1;
                                if (j < 0 || j >= tabOrder.length) return;
                                applyTabOrder(moveTab(tabOrder, tab.key, tabOrder[j]));
                            }}
                        >
                            <FontAwesomeIcon icon={tab.icon} className="device-type-tab-icon"/>
                            <span>{tab.label}</span>
                            <span className="device-type-tab-count">
                                {deviceTabCounts?.[tab.key] ?? 0}
                            </span>
                        </button>
                    ))}
                    {!isDefaultTabOrder && (
                        <button
                            type="button"
                            className="device-type-tab-reset"
                            title="Reset tab order to default"
                            aria-label="Reset tab order to default"
                            onClick={() => applyTabOrder(resetTabOrder(), false)}
                        >
                            <FontAwesomeIcon icon={faRotate}/>
                        </button>
                    )}
                </div>

                {/* Per-device-type quick actions (points 7 & 8) */}
                {deviceTypeActions}

                {/* QUICK FILTER BAR */}
                <div className="quick-filter-bar">
                    {currentFilters.map((filter) => (
                        <button
                            key={filter.key}
                            className={`quick-filter-tab ${quickFilter === filter.key ? "active" : ""}`}
                            onClick={() => {
                                const next = quickFilter === filter.key && filter.key !== "DEFAULT" ? "DEFAULT" : filter.key;
                                console.debug("[Dashboard] Quick filter →", next, `(tab: ${activeDeviceTab})`);
                                setQuickFilter(next);
                            }}
                            title={filter.tooltip}
                        >
                            <FontAwesomeIcon icon={filter.icon} className="quick-filter-icon"/>
                            <span className="quick-filter-label">{filter.label}</span>
                            <span className="quick-filter-count">
                                {tabQuickFilterCounts[filter.countKey] ?? 0}
                            </span>
                        </button>
                    ))}
                </div>

                {/* LABEL FILTER (passed from the dashboard) */}
                {labelFilter && (
                    <div className="device-label-filter">
                        <span className="device-label-filter-title">Filter by label</span>
                        {labelFilter}
                    </div>
                )}

                {/* LIST HEADER WITH SEARCH & CONTROLS */}
                <div className="section-title">
                    <span style={{fontSize: "1rem", fontWeight: "600", color: "#374151", display: "flex", alignItems: "center", gap: "10px"}}>
                        {/* Select-all across the currently filtered locks */}
                        {bulkSelectionEnabled && sortedDevices.length > 0 && (
                            <label
                                style={{display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600, color: "#5b6270"}}
                                title="Select all locks matching the current filters"
                            >
                                <input
                                    type="checkbox"
                                    checked={allFilteredSelected}
                                    ref={(el) => { if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected; }}
                                    onChange={toggleSelectAllFiltered}
                                    style={{width: 16, height: 16, cursor: "pointer"}}
                                />
                                Select all
                            </label>
                        )}
                        <span>
                            Asset List{" "}
                            <span style={{fontSize: "0.85rem", color: "#9ca3af", fontWeight: "400"}}>
                                ({effectiveDevices.length})
                            </span>
                        </span>
                    </span>
                    <div className="section-controls-right">
                        {/* Search Bar */}
                        <div className="search-pill-wrapper" style={{minWidth: '250px', maxWidth: '300px'}}>
                            <input
                                type="text"
                                className="search-pill-input"
                                placeholder="Search Name or ID..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <button className="search-pill-btn">
                                <FontAwesomeIcon icon={faSearch}/>
                            </button>
                        </div>

                        {/* Clear Filters Button */}
                        {isFilterActive && (
                            <button className="clear-filters-btn-compact" onClick={onResetFilters} title="Reset all filters">
                                <FontAwesomeIcon icon={faTimes}/>
                            </button>
                        )}

                        {/* View Toggle */}
                        <div className="view-toggle-group">
                            <button onClick={() => { console.debug("[Dashboard] View mode → GRID"); setViewMode("GRID"); }} className={`toggle-btn ${viewMode === "GRID" ? "active" : ""}`} title="Grid View">
                                <FontAwesomeIcon icon={faLayerGroup}/> <span style={{marginLeft: '5px', fontSize: '0.85rem'}}>Grid</span>
                            </button>
                            <button onClick={() => { console.debug("[Dashboard] View mode → LIST"); setViewMode("LIST"); }} className={`toggle-btn ${viewMode === "LIST" ? "active" : ""}`} title="List View">
                                <FontAwesomeIcon icon={faList}/> <span style={{marginLeft: '5px', fontSize: '0.85rem'}}>List</span>
                            </button>
                        </div>

                        {/* GT06 Help Button */}
                        {activeDeviceTab === "GT06_TRACKER" && (
                            <button
                                onClick={() => setShowGt06Help(true)}
                                className="icon-btn"
                                title="GPS Tracker Setup Guide"
                                style={{color: "var(--color-primary)"}}
                            >
                                <FontAwesomeIcon icon={faCircleQuestion} size="lg"/>
                            </button>
                        )}

                        {/* Refresh Button */}
                        <button
                            onClick={handleRefresh}
                            className="icon-btn"
                            title="Refresh Location"
                            disabled={isRefreshingLocation || isFetchingMobiTrack}
                        >
                            <FontAwesomeIcon icon={faRotate} size="lg" spin={isRefreshingLocation || isFetchingMobiTrack}/>
                        </button>
                    </div>
                </div>

                {/* BULK SELECTION ACTION BAR (GPS Lock tab, ≥1 selected) */}
                {bulkSelectionEnabled && bulkSelectedIds.size > 0 && (
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: "12px", padding: "10px 14px", margin: "0 0 10px",
                        background: "#FFF1DC", border: "1px solid #FFD3A8", borderRadius: "8px",
                    }}>
                        <span style={{fontWeight: 600, color: "#7c3a00", fontSize: "0.88rem"}}>
                            {bulkSelectedIds.size} lock{bulkSelectedIds.size > 1 ? "s" : ""} selected
                        </span>
                        <div style={{display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end"}}>
                            <button
                                onClick={() => setBulkSelectedIds(new Set())}
                                style={{
                                    padding: "6px 12px", border: "1px solid #d1d5db", borderRadius: "6px",
                                    background: "#fff", color: "#374151", cursor: "pointer", fontSize: "0.82rem",
                                }}
                            >
                                Clear
                            </button>
                            {canInstantUnlock && (
                                <button
                                    onClick={instantUnlockSelected}
                                    disabled={!!passcodeFeature}
                                    title={passcodeFeature
                                        ? "Passcode verification is required per lock — unlock these from each lock's panel"
                                        : "Unlock the selected online locks now"}
                                    style={{
                                        padding: "6px 14px", borderRadius: "6px",
                                        border: passcodeFeature ? "1px solid #d1d5db" : "1px solid #363166",
                                        background: passcodeFeature ? "#f3f4f6" : "#fff",
                                        color: passcodeFeature ? "#9ca3af" : "#363166",
                                        cursor: passcodeFeature ? "not-allowed" : "pointer",
                                        fontSize: "0.82rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faLockOpen} />
                                    Instant Unlock
                                </button>
                            )}
                            {canScheduleUnlock && (
                                <button
                                    onClick={scheduleSelectedLocks}
                                    style={{
                                        padding: "6px 14px", border: "none", borderRadius: "6px",
                                        background: "#FF683A", color: "#fff", cursor: "pointer",
                                        fontSize: "0.82rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px",
                                    }}
                                >
                                    <FontAwesomeIcon icon={faClock} />
                                    Schedule Unlock
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* SCROLLABLE CONTENT AREA */}
                <div className="device-content-scroll-area">
                    {effectiveDevices.length === 0 && (
                        <div style={{
                            textAlign: "center", width: "100%", padding: "40px 20px",
                            color: "#9ca3af", backgroundColor: "#f9fafb", borderRadius: "8px",
                            border: "1px dashed #e5e7eb", marginTop: "20px",
                        }}>
                            <FontAwesomeIcon icon={faSearch} size="2x" style={{marginBottom: "10px", opacity: 0.5}}/>
                            <div>No devices match your current filters.</div>
                            <button
                                onClick={onResetFilters}
                                style={{marginTop: "10px", color: "#FF683A", background: "none", border: "none", cursor: "pointer", textDecoration: "underline"}}
                            >
                                Clear Filters
                            </button>
                        </div>
                    )}

                    {/* Grid View */}
                    {effectiveDevices.length > 0 && viewMode === "GRID" && (
                        <div className="device-card-grid">
                            {paginatedDevices.map((device, index) => (
                                <div
                                    key={index}
                                    className={`device-card ${selectedDeviceId === device.assetUnitId ? "selected" : ""}`}
                                    onClick={() => onRowClick(device)}
                                >
                                    <div className="card-header">
                                        <div style={{display: "flex", alignItems: "center", gap: "8px", minWidth: 0}}>
                                            {bulkSelectionEnabled && (
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Select ${device.name} for scheduled unlock`}
                                                    checked={bulkSelectedIds.has(device.assetUnitId)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => toggleBulkSelect(device.assetUnitId, e)}
                                                    style={{width: 16, height: 16, cursor: "pointer", flexShrink: 0}}
                                                />
                                            )}
                                            <div style={{minWidth: 0}}>
                                                <h3 className="card-title">{device.name}</h3>
                                                <span className="card-subtitle">{device.assetUnitId}</span>
                                            </div>
                                        </div>
                                        {(activeDeviceTab === "GPS_LOCK" || activeDeviceTab === "MOBI_TRACK" || activeDeviceTab === "GT06_TRACKER") && (
                                            <div className={`status-badge ${getDeviceStatus(device) === "ONLINE" ? "badge-online" : "badge-offline"}`}>
                                                {getDeviceStatus(device)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-body">
                                        {activeDeviceTab === "GPS_LOCK" && (
                                            <>
                                                <div className="info-row">
                                                    <span className="info-label"><FontAwesomeIcon icon={faBatteryHalf}/></span>
                                                    <span className="info-value">{device.batteryStatusF3 || "Unknown"}</span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-label"><FontAwesomeIcon icon={faTruckFast}/></span>
                                                    <span className="info-value" style={{fontSize: "0.85rem"}}>{device.assetMovementStatusF5}</span>
                                                </div>
                                                <div className="info-row">
                                                    <span className="info-value">{getLockBadge(device.lockStatusF4)}</span>
                                                </div>
                                            </>
                                        )}
                                        {activeDeviceTab === "BT_LOCK" && (
                                            <div className="info-row">
                                                <span className="info-value">{getLockBadge(device.lockStatusF4)}</span>
                                            </div>
                                        )}
                                        {activeDeviceTab === "GT06_TRACKER" && (
                                            <>
                                                {!isFt920(device) && (
                                                    <div className="info-row">
                                                        <span className="info-value">{getEngineBadge(device.accOn, getDeviceStatus(device) === DEVICE_ONLINE)}</span>
                                                    </div>
                                                )}
                                                {getDeviceStatus(device) === DEVICE_ONLINE && device.speedKmph != null && (
                                                    <div className="info-row">
                                                        <span className="info-label"><FontAwesomeIcon icon={faTruckFast}/></span>
                                                        <span className="info-value" style={{fontSize: "0.85rem"}}>{device.speedKmph} km/h</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <div className="info-row full-width location-row">
                                            <span className="info-label"><FontAwesomeIcon icon={faMapLocationDot}/></span>
                                            <span className="info-value location-text" title={getDisplayLocation(device)}>
                                                {getDisplayLocation(device)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-footer">
                                        <div className="footer-date">{getDeviceLastUpdate(device)}</div>
                                        {tripMap[device.assetUnitId] && (
                                            <div style={{fontSize:"0.7rem",color:"#1a73e8",fontWeight:700,display:"flex",alignItems:"center",gap:"3px"}}>
                                                <FontAwesomeIcon icon={faRoute}/>
                                                {tripMap[device.assetUnitId]}
                                            </div>
                                        )}
                                        {showAlertBell(device) && (
                                            <div className="footer-alert">{device.alertStatus}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* List View — virtualized (renders only on-screen rows; scales to thousands) */}
                    {effectiveDevices.length > 0 && viewMode === "LIST" && (
                        <div className="vdl-wrap">
                            <AutoSizer>
                                {({width, height}) => (
                                    <VirtualList
                                        width={width}
                                        height={height}
                                        rowCount={sortedDevices.length}
                                        rowHeight={68}
                                        overscanRowCount={8}
                                        rowRenderer={({index, key, style}) => {
                                            const device = sortedDevices[index];
                                            const status = getDeviceStatus(device);
                                            const online = status === DEVICE_ONLINE || status === "ONLINE";
                                            const loc = getDisplayLocation(device);
                                            const isLocked = device.lockStatusF4 === "Locked" || device.lockStatusF4 === LOCKED;
                                            const hasLock = device.lockStatusF4 && device.lockStatusF4 !== "N/A" && (activeDeviceTab === "GPS_LOCK" || activeDeviceTab === "BT_LOCK");
                                            const movement = device.assetMovementStatusF5 && device.assetMovementStatusF5 !== "N/A"
                                                ? device.assetMovementStatusF5
                                                : (activeDeviceTab === "GT06_TRACKER" && online && device.speedKmph != null ? `${device.speedKmph} km/h` : null);
                                            return (
                                                <div key={key} style={style} className="vdl-row-wrap">
                                                    <div
                                                        className={`vdl-card ${online ? "is-online" : "is-offline"} ${selectedDeviceId === device.assetUnitId ? "selected" : ""}`}
                                                        onClick={() => onRowClick(device)}
                                                    >
                                                        {/* 0. Bulk-select checkbox (GPS Lock tab) */}
                                                        {bulkSelectionEnabled && (
                                                            <div
                                                                className="vdl-col"
                                                                style={{flex: "0 0 auto", paddingRight: 4}}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    aria-label={`Select ${device.name} for scheduled unlock`}
                                                                    checked={bulkSelectedIds.has(device.assetUnitId)}
                                                                    onChange={(e) => toggleBulkSelect(device.assetUnitId, e)}
                                                                    style={{width: 16, height: 16, cursor: "pointer"}}
                                                                />
                                                            </div>
                                                        )}
                                                        {/* 1. Name / ID */}
                                                        <div className="vdl-col vdl-col-name">
                                                            <div className="vdl-name" title={device.name}>{device.name}</div>
                                                            <div className="vdl-sub">ID: {device.assetUnitId}</div>
                                                        </div>
                                                        {/* 2. Online status + battery */}
                                                        <div className="vdl-col vdl-col-status">
                                                            {activeDeviceTab !== "BT_LOCK" && (
                                                                <span className={`vdl-pill ${online ? "on" : "off"}`}>
                                                                    <span className="vdl-dot" />{online ? "Online" : "Offline"}
                                                                </span>
                                                            )}
                                                            <span className="vdl-battery">
                                                                <FontAwesomeIcon icon={faBatteryHalf} />
                                                                {device.batteryStatusF3 && device.batteryStatusF3 !== "Unknown" ? device.batteryStatusF3 : "—"}
                                                            </span>
                                                        </div>
                                                        {/* 3. Lock + movement */}
                                                        <div className="vdl-col vdl-col-state">
                                                            {hasLock && (
                                                                <span className={`vdl-lock ${isLocked ? "locked" : "unlocked"}`}>
                                                                    <FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />
                                                                    {isLocked ? "Locked" : "Unlocked"}
                                                                </span>
                                                            )}
                                                            {activeDeviceTab === "GT06_TRACKER" && !isFt920(device) && getEngineBadge(device.accOn, online)}
                                                            {movement && (
                                                                <span className="vdl-move"><FontAwesomeIcon icon={faRoute} />{movement}</span>
                                                            )}
                                                        </div>
                                                        {/* 4. Location + last ping */}
                                                        <div className="vdl-col vdl-col-loc">
                                                            <div className="vdl-loc" title={loc}>{loc}</div>
                                                            <div className="vdl-time">{getDeviceLastUpdate(device)}</div>
                                                        </div>
                                                        {/* 5. Unread alerts — same bell as the grid card footer.
                                                             Hidden for MOBI_TRACK: the duty alerts that light it
                                                             can't be rendered by the lock-only detail popup yet. */}
                                                        {showAlertBell(device) && (
                                                            <div
                                                                className="vdl-alert"
                                                                title="View alerts"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {device.alertStatus}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        }}
                                    />
                                )}
                            </AutoSizer>
                        </div>
                    )}
                </div>

                {/* PAGINATION (Grid view only; List view is virtualized) */}
                {effectiveDevices.length > 0 && viewMode === "GRID" && (
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 10px', borderTop: '1px solid #e5e7eb', backgroundColor: '#ffffff',
                        gap: '12px', flexWrap: 'wrap', minHeight: '48px',
                    }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                            <span style={{fontSize: '0.8rem', color: '#6b7280'}}>Show</span>
                            <select
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                style={{padding: '4px 28px 4px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.8rem', color: '#374151', backgroundColor: '#ffffff', cursor: 'pointer', outline: 'none', height: '32px'}}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span style={{fontSize: '0.8rem', color: '#6b7280'}}>per page</span>
                        </div>
                        <div style={{fontSize: '0.8rem', color: '#6b7280'}}>
                            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} assets
                        </div>
                        <div style={{display: 'flex', gap: '2px', alignItems: 'center'}}>
                            <button onClick={() => goToPage(1)} disabled={currentPage === 1} style={{padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ffffff', color: currentPage === 1 ? '#9ca3af' : '#374151', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', opacity: currentPage === 1 ? 0.5 : 1, height: '32px', minWidth: '32px'}} title="First page">
                                <FontAwesomeIcon icon={faAnglesLeft}/>
                            </button>
                            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} style={{padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ffffff', color: currentPage === 1 ? '#9ca3af' : '#374151', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', opacity: currentPage === 1 ? 0.5 : 1, height: '32px', minWidth: '32px'}} title="Previous page">
                                <FontAwesomeIcon icon={faChevronLeft}/>
                            </button>
                            {getPageNumbers().map((page, idx) => (
                                <React.Fragment key={idx}>
                                    {page === '...' ? (
                                        <span style={{padding: '6px 10px', color: '#9ca3af', fontSize: '0.8rem', height: '32px', display: 'flex', alignItems: 'center'}}>...</span>
                                    ) : (
                                        <button onClick={() => goToPage(page)} style={{padding: '6px 12px', border: '1px solid', borderColor: currentPage === page ? '#FF683A' : '#d1d5db', borderRadius: '6px', backgroundColor: currentPage === page ? '#FF683A' : '#ffffff', color: currentPage === page ? '#ffffff' : '#374151', cursor: 'pointer', fontSize: '0.8rem', fontWeight: currentPage === page ? '600' : '400', minWidth: '36px', height: '32px', transition: 'all 0.2s'}}>
                                            {page}
                                        </button>
                                    )}
                                </React.Fragment>
                            ))}
                            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} style={{padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#ffffff', color: currentPage === totalPages ? '#9ca3af' : '#374151', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', opacity: currentPage === totalPages ? 0.5 : 1, height: '32px', minWidth: '32px'}} title="Next page">
                                <FontAwesomeIcon icon={faChevronRight}/>
                            </button>
                            <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} style={{padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#ffffff', color: currentPage === totalPages ? '#9ca3af' : '#374151', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', opacity: currentPage === totalPages ? 0.5 : 1, height: '32px', minWidth: '32px'}} title="Last page">
                                <FontAwesomeIcon icon={faAnglesRight}/>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeviceList;
